import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerService } from '../services/api.service';
import {
  Users,
  Search,
  RefreshCw,
  Mail,
  Phone,
  Calendar,
  ShoppingBag,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  DollarSign
} from 'lucide-react';

const ITEMS_PER_PAGE = 10;

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const res = await customerService.getCustomers();
      setCustomers(res.customers || []);
    } catch (err) {
      console.warn("Failed to load admin customers:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Metrics Calculations
  const totalCustomers = customers.length;
  const totalOrdersCount = customers.reduce((sum, c) => sum + parseInt(c.orders_count || 0, 10), 0);
  const totalLifetimeSpend = customers.reduce((sum, c) => sum + parseFloat(c.total_spent || 0), 0);

  // Filtering Logic
  const filteredCustomers = customers.filter(c => {
    const term = search.toLowerCase();
    const name = (c.name || '').toLowerCase();
    const email = (c.email || '').toLowerCase();
    const phone = (c.phone || '').toLowerCase();

    return name.includes(term) || email.includes(term) || phone.includes(term);
  });

  // Pagination Math
  const totalPages = Math.ceil(filteredCustomers.length / ITEMS_PER_PAGE) || 1;
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentCustomers = filteredCustomers.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Top Banner & Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>
            Registered Customers & Users ({totalCustomers} Total Accounts)
          </h2>
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
            Monitor registered customer profiles, their total order counts, lifetime spend, and registration dates.
          </p>
        </div>

        <button onClick={fetchCustomers} className="btn-secondary" style={{ width: 'auto', padding: '0 16px', height: '40px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <RefreshCw size={15} />
          <span>Refresh Customers</span>
        </button>
      </div>

      {/* METRICS OVERVIEW CARDS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
        
        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Registered Customers</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#eff6ff', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Users size={16} />
            </div>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a' }}>{totalCustomers}</div>
          <div style={{ fontSize: '12px', color: '#3b82f6', fontWeight: 700, marginTop: '4px' }}>Active User Accounts</div>
        </div>

        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Orders Placed</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fef2f2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ShoppingBag size={16} />
            </div>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a' }}>{totalOrdersCount}</div>
          <div style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px' }}>Orders Across All Users</div>
        </div>

        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Customer Lifetime Value</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#ecfdf5', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DollarSign size={16} />
            </div>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#dc2626' }}>£{totalLifetimeSpend.toFixed(2)}</div>
          <div style={{ fontSize: '12px', color: '#10b981', fontWeight: 700, marginTop: '4px' }}>Combined Sales Revenue</div>
        </div>

      </div>

      {/* SEARCH BAR */}
      <div className="table-card-box" style={{ padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          
          <div className="header-search-wrapper" style={{ width: '380px', display: 'block' }}>
            <input
              type="text"
              className="header-search-input"
              placeholder="Search by customer name, email or phone..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            />
            <Search className="header-search-icon" size={16} />
          </div>

        </div>

        {/* CUSTOMERS TABLE */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b', textAlign: 'left' }}>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>CUSTOMER NAME</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>EMAIL ADDRESS</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>PHONE</th>
                <th style={{ padding: '12px 16px', fontWeight: 800, textAlign: 'center' }}>ORDERS COUNT</th>
                <th style={{ padding: '12px 16px', fontWeight: 800, textAlign: 'right' }}>TOTAL SPENT</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>JOINED SINCE</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>ACCOUNT STATUS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>Loading customer profiles...</td></tr>
              ) : currentCustomers.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>No registered customers found.</td></tr>
              ) : (
                currentCustomers.map((c) => {
                  const joinedDate = c.created_at
                    ? new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : 'Aug 2026';

                  return (
                    <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '14px 16px', fontWeight: 900, color: '#0f172a' }}>
                        {c.name || 'Valued Customer'}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <a href={`mailto:${c.email}`} style={{ color: '#dc2626', textDecoration: 'none', fontWeight: 600 }}>
                          {c.email}
                        </a>
                      </td>
                      <td style={{ padding: '14px 16px', color: '#64748b' }}>
                        {c.phone || 'N/A'}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        {parseInt(c.orders_count || 0, 10) > 0 ? (
                          <span
                            onClick={() => navigate(`/admin/orders?customer=${encodeURIComponent(c.email || c.name)}`)}
                            title={`View ${c.name || c.email}'s orders`}
                            style={{
                              background: '#fef2f2',
                              border: '1px solid #dc2626',
                              color: '#dc2626',
                              fontWeight: 900,
                              padding: '4px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'inline-block',
                              boxShadow: '0 2px 4px rgba(220, 38, 38, 0.1)',
                              transition: 'transform 0.15s ease'
                            }}
                          >
                            {c.orders_count} {parseInt(c.orders_count, 10) === 1 ? 'Order' : 'Orders'} →
                          </span>
                        ) : (
                          <span style={{ background: '#f8fafc', border: '1px solid #e2e8f0', color: '#94a3b8', fontWeight: 700, padding: '3px 10px', borderRadius: '6px', fontSize: '12px' }}>
                            0 Orders
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px', fontWeight: 900, color: '#0f172a', textAlign: 'right' }}>
                        £{parseFloat(c.total_spent || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '14px 16px', color: '#64748b', fontSize: '12px' }}>
                        {joinedDate}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 800, color: '#15803d', background: '#dcfce7', border: '1px solid #bbf7d0', padding: '3px 8px', borderRadius: '6px' }}>
                          Active Member
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
              Page {currentPage} of {totalPages}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#ffffff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
              >
                <ChevronLeft size={16} />
                <span>Previous</span>
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#ffffff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
              >
                <span>Next</span>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

      </div>

    </div>
  );
};

export default Customers;
